/**
 * Funções de máscaras de entrada para formatação automática
 * Aplica máscaras enquanto o usuário digita
 * 
 * Formatos de telefone brasileiro suportados:
 * - Fixo: (DD) XXXX-XXXX (10 dígitos, começa com 2,3,4,5)
 * - Celular: (DD) 9XXXX-XXXX (11 dígitos, começa com 9)
 * - 0800: 0800-XXX-XXXX (11 dígitos, ligação gratuita)
 * - 4003: 4003-XXXX (8 dígitos, tarifa local)
 * - 400X/300X: XXXX-XXXX (7-8 dígitos, atendimento nacional/local)
 */

/**
 * Verifica se o número é um telefone especial brasileiro (sem código de país)
 * Tipos especiais: 0800, 4003, 400X, 300X
 * Estes números NÃO devem receber prefixo +55
 */
export function isSpecialPhoneNumber(digits: string): boolean {
  if (!digits) return false;
  return (
    digits.startsWith('0800') ||
    digits.startsWith('4003') ||
    digits.startsWith('400') ||
    digits.startsWith('300')
  );
}

/**
 * Aplica máscara de telefone brasileiro inteligente
 * Detecta automaticamente o tipo de número pelo prefixo
 */
export function applyPhoneMask(value: string): string {
  // Remove tudo que não é dígito
  const digits = value.replace(/\D/g, '');
  
  if (digits.length === 0) return '';
  
  // Detecta 0800: 0800-XXX-XXXX (ligação gratuita)
  if (digits.startsWith('0800')) {
    const limited = digits.slice(0, 11);
    if (limited.length <= 4) return limited;
    if (limited.length <= 7) return `${limited.slice(0, 4)}-${limited.slice(4)}`;
    return `${limited.slice(0, 4)}-${limited.slice(4, 7)}-${limited.slice(7, 11)}`;
  }
  
  // Detecta 4003: 4003-XXXX (tarifa local nacional)
  if (digits.startsWith('4003')) {
    const limited = digits.slice(0, 8);
    if (limited.length <= 4) return limited;
    return `${limited.slice(0, 4)}-${limited.slice(4, 8)}`;
  }
  
  // Detecta 400X/300X (exceto 4003): XXXX-XXXX (atendimento)
  if (digits.startsWith('400') || digits.startsWith('300')) {
    const limited = digits.slice(0, 8);
    if (limited.length <= 4) return limited;
    return `${limited.slice(0, 4)}-${limited.slice(4, 8)}`;
  }
  
  // Padrão: telefone com DDD (fixo ou celular)
  const limited = digits.slice(0, 11);
  
  // DDD (2 primeiros dígitos)
  if (limited.length <= 2) return `(${limited}`;
  
  const ddd = limited.slice(0, 2);
  const numero = limited.slice(2);
  
  // Celular (começa com 9, 11 dígitos total)
  if (numero.startsWith('9') && numero.length > 4) {
    if (numero.length <= 5) return `(${ddd}) ${numero}`;
    return `(${ddd}) ${numero.slice(0, 5)}-${numero.slice(5, 9)}`;
  }
  
  // Fixo (8 dígitos após DDD)
  if (numero.length <= 4) return `(${ddd}) ${numero}`;
  return `(${ddd}) ${numero.slice(0, 4)}-${numero.slice(4, 8)}`;
}

/**
 * Aplica máscara de CPF
 * Formato: 123.456.789-00
 */
export function applyCPFMask(value: string): string {
  // Remove tudo que não é dígito
  const digits = value.replace(/\D/g, '');
  
  // Limita a 11 dígitos
  const limited = digits.slice(0, 11);
  
  // Aplica máscara progressiva
  if (limited.length === 0) return '';
  if (limited.length <= 3) return limited;
  if (limited.length <= 6) return `${limited.slice(0, 3)}.${limited.slice(3)}`;
  if (limited.length <= 9) {
    return `${limited.slice(0, 3)}.${limited.slice(3, 6)}.${limited.slice(6)}`;
  }
  return `${limited.slice(0, 3)}.${limited.slice(3, 6)}.${limited.slice(6, 9)}-${limited.slice(9, 11)}`;
}

/**
 * Aplica máscara de CNPJ
 * Formato: 12.345.678/0001-90
 */
export function applyCNPJMask(value: string): string {
  // Remove tudo que não é dígito
  const digits = value.replace(/\D/g, '');
  
  // Limita a 14 dígitos
  const limited = digits.slice(0, 14);
  
  // Aplica máscara progressiva
  if (limited.length === 0) return '';
  if (limited.length <= 2) return limited;
  if (limited.length <= 5) return `${limited.slice(0, 2)}.${limited.slice(2)}`;
  if (limited.length <= 8) {
    return `${limited.slice(0, 2)}.${limited.slice(2, 5)}.${limited.slice(5)}`;
  }
  if (limited.length <= 12) {
    return `${limited.slice(0, 2)}.${limited.slice(2, 5)}.${limited.slice(5, 8)}/${limited.slice(8)}`;
  }
  return `${limited.slice(0, 2)}.${limited.slice(2, 5)}.${limited.slice(5, 8)}/${limited.slice(8, 12)}-${limited.slice(12, 14)}`;
}

/**
 * Aplica máscara de data
 * Formato: DD/MM/AAAA
 */
export function applyDateMask(value: string): string {
  // Remove tudo que não é dígito
  const digits = value.replace(/\D/g, '');
  
  // Limita a 8 dígitos
  const limited = digits.slice(0, 8);
  
  // Aplica máscara progressiva
  if (limited.length === 0) return '';
  if (limited.length <= 2) return limited;
  if (limited.length <= 4) return `${limited.slice(0, 2)}/${limited.slice(2)}`;
  return `${limited.slice(0, 2)}/${limited.slice(2, 4)}/${limited.slice(4, 8)}`;
}

/**
 * Função principal que aplica a máscara baseada no tipo
 */
export function applyMask(
  value: string,
  maskType: 'phone' | 'cpf' | 'cnpj' | 'date' | null
): string {
  if (!maskType) return value;
  
  switch (maskType) {
    case 'phone':
      return applyPhoneMask(value);
    case 'cpf':
      return applyCPFMask(value);
    case 'cnpj':
      return applyCNPJMask(value);
    case 'date':
      return applyDateMask(value);
    default:
      return value;
  }
}

/**
 * Retorna placeholder adequado para cada tipo de máscara
 */
export function getMaskPlaceholder(maskType: 'phone' | 'cpf' | 'cnpj' | 'date' | null): string {
  switch (maskType) {
    case 'phone':
      return '(11) 99999-9999';
    case 'cpf':
      return '123.456.789-00';
    case 'cnpj':
      return '12.345.678/0001-90';
    case 'date':
      return 'DD/MM/AAAA';
    default:
      return 'Digite sua resposta...';
  }
}

/**
 * Aplica máscara de moeda brasileira (R$)
 * Formato: R$ 1.234,56
 * Recebe valor em centavos como string ou valor decimal
 */
export function applyCurrencyMask(value: string | number): string {
  // Se for número, converte para string de centavos
  let digits: string;
  if (typeof value === 'number') {
    // Valor já em reais (ex: 297.00), converte para centavos
    digits = Math.round(value * 100).toString();
  } else {
    // Remove tudo que não é dígito
    digits = value.replace(/\D/g, '');
  }
  
  if (digits.length === 0 || digits === '0') return '';
  
  // Converte para número (centavos) e formata
  const numericValue = parseInt(digits, 10) / 100;
  
  return numericValue.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
}

/**
 * Remove máscara de moeda e retorna valor numérico em reais
 * Ex: "R$ 297,00" → 297.00
 */
export function parseCurrency(value: string): number {
  const digits = value.replace(/\D/g, '');
  if (digits.length === 0) return 0;
  return parseInt(digits, 10) / 100;
}

/**
 * Aplica máscara de CEP brasileiro
 * Formato: 12345-678
 * 
 * @param value - Valor a ser formatado
 * @returns String formatada com máscara de CEP
 */
export function applyCEPMask(value: string): string {
  // Remove tudo que não é dígito
  const digits = value.replace(/\D/g, '');
  
  // Limita a 8 dígitos
  const limited = digits.slice(0, 8);
  
  // Se vazio, retorna vazio
  if (limited.length === 0) return '';
  
  // Aplica máscara: XXXXX-XXX
  if (limited.length <= 5) {
    return limited;
  }
  
  return `${limited.slice(0, 5)}-${limited.slice(5, 8)}`;
}

/**
 * Formata CPF ou CNPJ para exibição (display, não input)
 * Detecta automaticamente pelo tamanho dos dígitos:
 * - 11 dígitos → CPF (XXX.XXX.XXX-XX)
 * - 14 dígitos → CNPJ (XX.XXX.XXX/XXXX-XX)
 * Se já estiver formatado (contém . ou -), retorna como está.
 * 
 * @param value - Valor do documento (com ou sem máscara)
 * @returns String formatada para exibição
 */
export function formatDocumentForDisplay(value: string | null | undefined): string {
  if (!value) return '';
  
  // Se já contém formatação, retorna como está
  if (value.includes('.') || value.includes('-') || value.includes('/')) {
    return value;
  }
  
  // Remove tudo que não é dígito
  const digits = value.replace(/\D/g, '');
  
  if (digits.length === 11) return applyCPFMask(digits);
  if (digits.length === 14) return applyCNPJMask(digits);
  
  // Tamanho não reconhecido, retorna original
  return value;
}
